import { useEffect } from "react";
import type { SeoData } from "@/services/pages";
import { useSiteSettings } from "@/services/siteSettings";

type PartialSeo = Partial<SeoData>;

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(graph: object[]) {
  const id = "seo-jsonld";
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (graph.length) {
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(graph.length === 1 ? graph[0] : { "@context": "https://schema.org", "@graph": graph });
  } else if (script) {
    script.remove();
  }
}

function plainText(v: unknown): string {
  if (!v) return "";
  return String(v).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * SPA head manager. Sets <title>, meta description/keywords/canonical, Open Graph
 * and Twitter tags, and auto-generates JSON-LD (Organization, WebPage,
 * BreadcrumbList, FAQPage when FAQ sections exist). Nothing is rendered.
 */
export default function Seo({ seo, path = "/" }: { seo?: PartialSeo; path?: string }) {
  const { get } = useSiteSettings();
  useEffect(() => {
    const data = seo || {};
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const canonical =
      data.canonical || `${window.location.origin}${base}${path.startsWith("/") ? path : `/${path}`}`;
    const siteName = get("site_name", "NOMADLIVINGS");
    const globalTitle = get("seo_title", "");
    const globalDesc = get("seo_description", "");
    const globalKeywords = get("seo_keywords", "");
    const globalOgImage = get("og_image_url", "");
    const title = data.title || globalTitle || document.title || siteName;
    const description = data.description || globalDesc;
    const keywords = data.keywords || globalKeywords;
    const ogImage = data.og_image || globalOgImage;

    document.title = title;
    if (description) upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow");
    upsertLink("canonical", canonical);

    // Open Graph + Twitter
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteName);
    if (ogImage) upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description || "");

    // JSON-LD structured data
    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "NOMADLIVINGS",
      url: `${window.location.origin}${base}`,
    };
    const webpage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      url: canonical,
      description: data.description || undefined,
    };
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}${base}/` },
        { "@type": "ListItem", position: 2, name: plainText(title) || "Page", item: canonical },
      ],
    };
    const graph: object[] = [org, webpage, breadcrumb];

    const faqItems = (data as unknown as { faq?: { q: string; a: string }[] }).faq;
    if (faqItems && faqItems.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    setJsonLd(graph);

    return () => {
      // Keep head tags (SPA navigation shouldn't wipe shared tags).
    };
  }, [seo, path]);

  return null;
}